/**
 * MilestoneRow — single step in the order investigation timeline.
 *
 * Renders: status icon | title | status badge | timestamp | summary
 * Clickable to expand/collapse the MilestoneDetailsPanel.
 */

import { useState } from 'react';
import { CheckCircle2, Circle, HelpCircle, AlertCircle, XCircle, ChevronDown } from 'lucide-react';
import type { InvestigationMilestone, MilestoneStatus } from '../../types/orderInvestigation';
import MilestoneDetailsPanel from './MilestoneDetailsPanel';
import { Badge, type BadgeTone } from '../ui/badge';
import { Button } from '@/components/ui/button';
import type { ProvenanceTier } from '../../config/colors';
import { formatUkFullDateTime } from '../../lib/calc';

interface MilestoneRowProps {
  milestone: InvestigationMilestone;
  isLast?: boolean;
}

// Tier colours come from the shared colors module via `Badge`'s
// `provenance` prop: an inference is not a confirmation — inferred is violet.
// `missing`/`contradictory` are not provenance tiers and take the nearest
// tone.
const STATUS_CONFIG: Record<MilestoneStatus, {
  icon: typeof CheckCircle2;
  iconClass: string;
  badge: { tone?: BadgeTone; provenance?: ProvenanceTier };
  label: string;
  lineClass: string;
}> = {
  observed: {
    icon: CheckCircle2,
    iconClass: 'text-success',
    badge: { provenance: 'observed' },
    label: 'Observed',
    lineClass: 'border-success/30',
  },
  declared: {
    icon: Circle,
    iconClass: 'text-ring',
    badge: { provenance: 'declared' },
    label: 'Declared',
    lineClass: 'border-ring/30',
  },
  inferred: {
    icon: HelpCircle,
    iconClass: 'text-provenance-inferred',
    badge: { provenance: 'inferred' },
    label: 'Inferred',
    lineClass: 'border-provenance-inferred/30',
  },
  missing: {
    icon: AlertCircle,
    iconClass: 'text-muted-foreground',
    badge: { tone: 'neutral' },
    label: 'Missing',
    lineClass: 'border-accent/30',
  },
  contradictory: {
    icon: XCircle,
    iconClass: 'text-destructive',
    badge: { tone: 'destructive' },
    label: 'Contradictory',
    lineClass: 'border-destructive/30',
  },
};

function formatTimestamp(iso?: string): string | null {
  if (!iso) return null;
  return formatUkFullDateTime(iso, iso);
}

export default function MilestoneRow({ milestone, isLast }: MilestoneRowProps) {
  const [expanded, setExpanded] = useState(false);
  const config = STATUS_CONFIG[milestone.status];
  const Icon = config.icon;
  const formattedTime = formatTimestamp(milestone.bestTimestamp);

  return (
    <div className="relative">
      {/* Vertical connector line */}
      {!isLast && (
        <div
          className={`absolute left-4 top-8 bottom-0 w-0 border-l-2 border-dashed ${config.lineClass}`}
        />
      )}

      {/* Clickable row */}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => setExpanded(!expanded)}
        className="w-full h-auto p-2 -ml-1 text-left items-start gap-3 rounded-lg hover:bg-secondary/30 group"
      >
        {/* Icon */}
        <div className="shrink-0 mt-0.5">
          <Icon className={`w-6 h-6 ${config.iconClass}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-white text-sm">{milestone.title}</span>
            <Badge {...config.badge}>
              {config.label}
            </Badge>
            {formattedTime && (
              <span className="text-xs text-muted-foreground font-mono">{formattedTime}</span>
            )}
            {/* Expand indicator */}
            <ChevronDown
              className={`w-3.5 h-3.5 text-accent group-hover:text-muted-foreground transition-transform duration-200 ml-auto shrink-0 ${
                expanded ? 'rotate-180' : ''
              }`}
            />
          </div>

          {/* Summary */}
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{milestone.summary}</p>

          {/* Expandable details */}
          <MilestoneDetailsPanel evidence={milestone.evidence} expanded={expanded} />
        </div>
      </Button>
    </div>
  );
}
